import React, {
  useEffect, useRef, type ReactNode
} from 'react';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import { isZendeskHiddenByConsumer } from './useZendeskMessaging';

const SNIPPET_URL = 'https://static.zdassets.com/ekr/snippet.js';
const SCRIPT_ID = 'ze-snippet';

type ZEFunction = (action: string, ...args: unknown[]) => unknown;

/** Returns the live window.zE reference (real SDK after it loads). */
function getZE(): ZEFunction | null {
  return (window as Window & { zE?: ZEFunction }).zE ?? null;
}

interface ZendeskMessagingProviderProps {
  children: ReactNode;
  /** From Redux: user.user.zendeskConfig — present when user is eligible for Zendesk Messaging. */
  zendeskConfig?: User.ZendeskConfig | null;
}

/**
 * Polls until window.zE is available (set by the Zendesk snippet).
 *
 * The snippet creates zE as a lightweight command-queue stub. Commands pushed to
 * it (loginUser, messenger:on, etc.) are queued and replayed once the full SDK
 * bundle loads. This is the standard Zendesk integration pattern — the stub is
 * safe to use for issuing commands.
 */
function waitForZE(maxMs = 10000): Promise<(action: string, ...args: unknown[]) => unknown> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const id = window.setInterval(() => {
      const { zE } = (window as Window & { zE?: (a: string, ...args: unknown[]) => unknown });
      if (zE) {
        window.clearInterval(id);
        resolve(zE);
        return;
      }
      if (Date.now() - start > maxMs) {
        window.clearInterval(id);
        reject(new Error('Zendesk zE not available after timeout'));
      }
    }, 100);
  });
}

/**
 * Loads Zendesk Web Widget script.
 * If window.zE is already available (script loaded in a previous session), resolves immediately.
 * If the script tag exists but window.zE is missing (previous load failed), removes the stale tag and retries.
 * If the script exists with a different channelKey, removes the old script and reloads with the new key.
 */
function loadScript(channelKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as Window & { zE?: unknown }).zE) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.channelKey === channelKey) {
        const { zE } = (window as Window & { zE?: unknown });
        if (zE) {
          resolve();
          return;
        }
        // Script tag exists but zE was never defined — previous load failed. Remove and retry.
        existing.remove();
      } else {
        existing.remove();
        delete (window as Window & { zE?: unknown }).zE;
      }
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.dataset.channelKey = channelKey;
    script.src = `${ SNIPPET_URL }?key=${ encodeURIComponent(channelKey) }`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Zendesk script'));
    document.head.appendChild(script);
  });
}

/**
 * ZendeskMessagingProvider — loads Zendesk SDK and authenticates user (loginUser).
 *
 * Mount once near the root (e.g. inside RoutesComponent next to FirebaseMessagingProvider).
 *
 * Flow:
 *   channelKey + jwtToken present → load script → loginUser(jwtToken) → show widget
 *   channelKey or jwtToken absent AND was previously logged in → logoutUser → hide widget
 */
function ZendeskMessagingProvider({
  children, zendeskConfig
}: ZendeskMessagingProviderProps) {
  const tagsRef = useRef(zendeskConfig?.tags);
  tagsRef.current = zendeskConfig?.tags;

  /** Tracks whether loginUser completed successfully at least once in this session. */
  const wasLoggedInRef = useRef(false);

  const channelKey = zendeskConfig?.channelKey ?? null;
  const jwtToken = zendeskConfig?.jwtToken ?? null;

  useEffect(() => {
    if (!channelKey || !jwtToken) {
      if (wasLoggedInRef.current) {
        wasLoggedInRef.current = false;
        const zE = getZE();
        if (zE) {
          try {
            zE('messenger', 'logoutUser');
            zE('messenger', 'hide');
          } catch (err) {
            log(LOG_LEVELS.ERROR, 'ZendeskMessaging', 'logoutUser failed', err);
          }
        }
      }
      return undefined;
    }

    let mounted = true;
    let loginTimeoutId: ReturnType<typeof setTimeout> | undefined;

    loadScript(channelKey)
      .then(() => waitForZE())
      .then((zE) => {
        if (!mounted) return;

        zE('messenger:set', 'zIndex', 24);

        // loginUser: the 3rd arg is the JWT provider callback — the only
        // callback guaranteed to fire by the Zendesk SDK. The 4th arg
        // (completion/error) is NOT part of the official API and does not
        // fire on production. Therefore all post-login setup happens inside
        // the JWT callback, right after providing the token.
        //
        // loginTimeout detects when the JWT callback never fires at all
        // (SDK bundle blocked by CSP or network failure).
        let jwtCallbackFired = false;
        loginTimeoutId = setTimeout(() => {
          if (!jwtCallbackFired && mounted) {
            log(LOG_LEVELS.ERROR, 'ZendeskMessaging', 'loginUser JWT callback not invoked within 15 s — Zendesk SDK bundle may be blocked (CSP/network)');
          }
        }, 15000);

        zE(
          'messenger',
          'loginUser',
          (callback: (token: string) => void) => {
            jwtCallbackFired = true;
            clearTimeout(loginTimeoutId);
            callback(jwtToken);
            if (!mounted) return;

            // By the time the JWT callback fires, the real Zendesk SDK has
            // replaced window.zE. The captured `zE` from waitForZE() may
            // still be the old command-queue stub whose queue is no longer
            // drained. Always use the live window.zE reference here.
            const zeReal = getZE();
            if (!zeReal) return;

            const tags = tagsRef.current;
            if (tags?.length) {
              zeReal('messenger:set', 'conversationTags', tags);
            }
            wasLoggedInRef.current = true;
            if (!isZendeskHiddenByConsumer()) {
              zeReal('messenger', 'show');
            }
          }
        );
      })
      .catch((err) => {
        if (mounted) log(LOG_LEVELS.ERROR, 'ZendeskMessaging', 'Zendesk init failed', err);
      });

    return () => {
      mounted = false;
      clearTimeout(loginTimeoutId);
    };
  }, [channelKey, jwtToken]);

  return <>{ children }</>;
}

export default ZendeskMessagingProvider;
